import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TILES, show } from './productUsage.js'

/**
 * ProductMetrics — how a product is being used, shown inside ProTrack.
 *
 * The product counts its own usage; ProTrack only reads the totals and displays them. Nothing is
 * stored here, and if the product is offline or hasn't built its counter yet, this says so in
 * plain words rather than showing zeros that look like real measurements.
 *
 * A missing figure shows as "—" on purpose. An absent number and a number that is genuinely zero
 * are different things, and a usage dashboard that blurs the two is worse than useless.
 */

/** What the figures are and where they come from, with the refresh button, and the reason when
 *  the product could not give them. The figures themselves are drawn by whoever holds this. */
export function UsageNote({ productName, metrics }) {
  const { t } = useTranslation('platform')
  const { state, busy, load } = metrics
  const data = state?.data || {}
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Usage — {productName}</p>
          <p className="text-[11px] text-gray-500">
            Counted by the product itself, and limited to what your role there may see.
            ProTrack reads the figures and stores nothing.
          </p>
        </div>
        <button onClick={load} disabled={busy}
          className="p-1.5 text-gray-400 hover:text-[var(--pt-accent,#4F46E5)] hover:bg-gray-50 rounded-lg disabled:opacity-40"
          title={t('t_refresh')}>
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {!state ? (
        <p className="mt-2 text-xs text-gray-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading usage…</p>
      ) : !state.available ? (
        <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {/* The reason already says what happened, in full, so it is shown as it is. */}
          <span>{state.reason || 'The product did not respond.'}</span>
          {state.temporary && (
            <button type="button" onClick={load} disabled={busy} className="ml-auto inline-flex items-center gap-1 font-semibold underline disabled:opacity-50">
              <RefreshCw className="w-3 h-3" /> Try again
            </button>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-gray-400 mt-1">
          {data.as_of && <>As of {new Date(data.as_of).toLocaleString()}. </>}
          {TILES.some(x => data[x.key] === null || data[x.key] === undefined) && (
            <>A dash means the product doesn't measure that yet — not that it is zero.</>
          )}
        </p>
      )}
    </div>
  )
}

/** The figures on a card of their own: for when there is no opening screen to put them on. */
export default function ProductMetrics({ productName, metrics }) {
  const { state } = metrics
  const data = state?.data || {}
  return (
    <div>
      <UsageNote productName={productName} metrics={metrics} />
      {state?.available && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {TILES.map(t => {
            const v = show(t.key, data[t.key])
            const absent = v === '—'
            return (
              <div key={t.key} className="rounded-xl border border-gray-200 border-l-4 border-l-[var(--pt-accent,#4F46E5)] bg-white px-3 py-3">
                <p className={`text-xl font-bold tabular-nums ${absent ? 'text-gray-300' : 'text-gray-900'}`}>{v}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{t.label}</p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
